import { useCallback } from 'react'

import {
  useFlag,
  useQueryParams,
  useSetQueryParams,
} from '@sketch/modules-common'

/**
 * The version named in `?compare`, or undefined when there is none or the
 * feature is off.
 *
 * The flag is checked here rather than by each caller: a URL carrying `compare`
 * outlives the flag being turned off - a link someone was sent, a tab left open
 * - and without the feature it has to mean nothing.
 */
export function useComparedShortId() {
  const isEnabled = useFlag('canvas-version-compare')
  const { compare } = useQueryParams<'SHARE_PAGE_CANVAS_VIEW'>()

  return {
    isEnabled,
    comparedShortId:
      isEnabled && typeof compare === 'string' ? compare : undefined,
  }
}

/**
 * The search to carry onto a link that moves around the share - another page,
 * frame, Section or version - so a comparison keeps running across it. Empty
 * when nothing is being compared.
 *
 * Only `compare`: the rest of the query belongs to the view it was set on,
 * such as the camera position.
 */
export function useCompareSearch() {
  const { comparedShortId } = useComparedShortId()

  return comparedShortId
    ? new URLSearchParams({ compare: comparedShortId }).toString()
    : ''
}

/**
 * Turns a comparison on and off from anywhere that lists versions.
 *
 * It lives in the URL rather than in state, so it survives opening a frame,
 * switching page and being pasted to someone else. Only the other version is
 * named; which of the two is older is worked out in useCompareVersions.
 */
export function useCompareToggle() {
  const { isEnabled, comparedShortId } = useComparedShortId()
  const setQueryParams = useSetQueryParams<'SHARE_PAGE_CANVAS_VIEW'>()

  const toggleCompare = useCallback(
    (versionShortId: string) => {
      setQueryParams({
        compare:
          comparedShortId === versionShortId ? undefined : versionShortId,
      })
    },
    [comparedShortId, setQueryParams]
  )

  const stopComparing = useCallback(() => {
    setQueryParams({ compare: undefined })
  }, [setQueryParams])

  return {
    isEnabled,
    comparedShortId,
    isComparing: !!comparedShortId,
    toggleCompare,
    stopComparing,
  }
}
